import { useState } from "react";
import { Modal, TextInput, Select, Group } from "@mantine/core";
import { Button, Alert } from "@xplatform/design-system";
import { IconX } from "@tabler/icons-react";
import type { CreateProviderRequest } from "@models/provider";

interface CreateProviderDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    onCreate: (data: CreateProviderRequest) => Promise<void>;
}

const PROVIDER_TYPES = [
    { value: "llm", label: "LLM" },
    { value: "ocr", label: "OCR" },
];

export const CreateProviderDialog = ({
    open,
    onClose,
    onSuccess,
    onCreate,
}: CreateProviderDialogProps) => {
    const [key, setKey] = useState("");
    const [type, setType] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setKey("");
        setType(null);
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);
        if (!key.trim()) {
            setError("Provider key is required");
            return;
        }
        if (!type) {
            setError("Provider type is required");
            return;
        }

        try {
            setLoading(true);
            await onCreate({ key: key.trim(), type });
            onSuccess?.();
            handleClose();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Failed to create provider",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            title="Add Provider"
            size="sm"
            portalProps={{ target: "#admin-portal-root" }}
        >
            {error && (
                <Alert color="red" mb="md" icon={<IconX size={16} />}>
                    {error}
                </Alert>
            )}

            <TextInput
                label="Provider Key"
                placeholder="e.g. openai, google, aws-textract"
                value={key}
                onChange={(e) => setKey(e.currentTarget.value)}
                required
                mb="md"
            />

            <Select
                label="Type"
                data={PROVIDER_TYPES}
                value={type}
                onChange={setType}
                required
                mb="md"
            />

            <Group justify="flex-end" mt="lg">
                <Button variant="default" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} loading={loading}>
                    Create
                </Button>
            </Group>
        </Modal>
    );
};
